import React, { useState } from "react";
import { Reveal } from "../Reveal";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Mail, Phone, Instagram, Send } from "lucide-react";
import { BeaconsIcon } from "../BeaconsIcon";
import { toast } from "sonner";
import { trackEvent, trackConversion } from "../../lib/analytics";

export const Contact = ({ data }) => {
  const contact = data?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/contact", form);
      trackConversion("contact_form_submit", {
        currency: "GBP",
        value: 1,
        method: "website_form",
      });
      toast.success(res.message || "Message sent!", {
        description: "We'll get back to you shortly.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      trackEvent("contact_form_error", {
        error: err.response?.status || "unknown",
      });
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactInfoClick = (label) => {
    const eventMap = {
      email: "email_click",
      phone: "phone_click",
      instagram: "social_click",
      beacons: "social_click",
    };
    trackEvent(eventMap[label.toLowerCase()] || "contact_info_click", { label });
  };

  const infoItems = [
    { icon: BeaconsIcon, label: "Beacons", value: "Services & Booking", href: contact.facebook },
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}` },
    { icon: Phone, label: "Phone", value: contact.phone, href: `tel:${contact.phone}` },
    { icon: Instagram, label: "Instagram", value: "@mixingdreamsltd", href: contact.instagram },
  ];

  return (
    <section id="contact" className="relative bg-void py-16 md:py-24" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left: heading + info */}
          <div>
            <Reveal className="mb-6 flex items-center gap-4">
              <span className="h-px w-12 bg-coral" />
              <span className="text-coral text-xs uppercase tracking-[0.3em]">Get In Touch</span>
            </Reveal>
            <Reveal>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                Let&rsquo;s craft something <span className="italic text-gold">extraordinary</span>
              </h2>
            </Reveal>

            <div className="mt-12 space-y-4">
              {infoItems.map((item, i) => (
                <Reveal key={item.label} delay={i * 0.08}>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`contact-info-${item.label.toLowerCase()}`}
                    onClick={() => handleContactInfoClick(item.label)}
                    className="group flex items-center gap-5 rounded-lg border border-line bg-surface p-5 transition-all duration-300 hover:border-gold/50 hover:-translate-y-0.5"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/10 text-gold transition-colors group-hover:bg-gold group-hover:text-black">
                      <item.icon size={20} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-widest text-smoke">
                        {item.label}
                      </span>
                      <span className="block text-ink group-hover:text-gold transition-colors">
                        {item.value}
                      </span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <Reveal delay={0.15}>
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl border border-line bg-surface p-8 md:p-10"
              data-testid="contact-form"
            >
              <div className="space-y-6">
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-widest text-smoke">
                    Name *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    data-testid="contact-name-input"
                    placeholder="Your name"
                    className="w-full rounded-lg border border-[#2a2a2a] bg-void px-4 py-3 text-ink placeholder:text-[#555] outline-none transition-colors focus:border-gold"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-widest text-smoke">
                    Email *
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    data-testid="contact-email-input"
                    placeholder="your@email.com"
                    className="w-full rounded-lg border border-[#2a2a2a] bg-void px-4 py-3 text-ink placeholder:text-[#555] outline-none transition-colors focus:border-gold"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-widest text-smoke">
                    Phone
                  </label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="contact-phone-input"
                    placeholder="+44 ..."
                    className="w-full rounded-lg border border-[#2a2a2a] bg-void px-4 py-3 text-ink placeholder:text-[#555] outline-none transition-colors focus:border-gold"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-widest text-smoke">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    placeholder="Tell us about your project or event..."
                    className="w-full resize-none rounded-lg border border-[#2a2a2a] bg-void px-4 py-3 text-ink placeholder:text-[#555] outline-none transition-colors focus:border-gold"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-widest text-black transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_-10px_rgba(255,200,0,0.6)] disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Message"}
                  <Send size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
